import React, { MouseEventHandler, useCallback, useState } from 'react';
import { commaFormat, addSignCommaFormat } from '../new-report/utils/formatter';
import CooperationStoppedModal from '../modal/CooperationStoppedModal';
import { AccountGroupProps, AssetBundleType, GroupAssetKey } from './Home';
import { amountToAmountColor } from '../utils/util';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { GetUserAssetResult } from 'config/api';
import * as api from 'config/api';
import { UserState } from 'hooks/useHomeUser';

type TotalCardPickerItemDomainProps = {
  amount: number;
  profitOrLoss: number;
  toggleAssetVisibility: () => void;
  canToggleAssetVisibility: boolean;
  isHiddenAsset: boolean;
  isOutOfDateBrokers: boolean;
  brokers: api.GetUserBrokerResultItem[];
  refetchBrokers: () => Promise<void>;
  isAuthenticatedMoneytree: boolean | null;
  isLinkedMoneytree: boolean | null;
  initialCardType: AssetBundleType;
};
type TotalCardPickerItemProps = TotalCardPickerItemDomainProps;

type CardPickerItemProps = {
  amount: number;
  profitOrLoss: number;
};
export type GroupCardPickerItemProps = CardPickerItemProps & {
  groupKey: GroupAssetKey;
  groupName: string;
};
type AddGroupCardPickerItemProps = {
  addGroup: () => void;
};

const getEyeStyle = (isHiddenAsset: boolean): string => {
  return isHiddenAsset
    ? 'icon-eye_close_img text-bluegray-400'
    : 'icon-eye_open_img text-bluegray-400';
};

const stopPropagation = <T extends Element>(
  func: MouseEventHandler<T>
): MouseEventHandler<T> => (event): void => {
  event.stopPropagation();
  func(event);
};

const getPointerStyle = (canClick: boolean): string => {
  return canClick ? 'cursor-pointer' : 'cursor-default';
};

const useIsShowModal = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const showModal = useCallback(() => setIsShowModal(true), []);
  const hideModal = useCallback(() => setIsShowModal(false), []);
  return { isShowModal, showModal, hideModal };
};

type CardPickerItemViewProps = {
  cautionElement?: React.ReactElement;
  amount: string;
  onClickEye: MouseEventHandler;
  profitOrLoss: { amount: string; color: string };
  eyeClassName: string;
};

const CardPickerItemView = ({
  cautionElement,
  amount,
  onClickEye,
  profitOrLoss,
  eyeClassName,
}: CardPickerItemViewProps) => {
  return (
    <div className="total-card">
      <div className="slick-item flex flex-col flex-1 bg-white rounded-md py-6 px-4 min-w-assetcard h-full shadow-card">
        <div className="text-center text-sm font-bold leading-5">資産総額</div>
        <div className="flex justify-center items-center leading-6">
          {cautionElement ?? <div className="mr-2 h-4 w-4"></div>}
          <div className="font-bold">
            <span className="text-xl mr-2">{amount}</span>
            <span className="text-xs">円</span>
          </div>
          <span className={`${eyeClassName} ml-2`} onClick={onClickEye}></span>
        </div>
        <div className="flex items-center text-xs mt-2 pt-1 px-2 border-t border-bluegray-300">
          <div className="text-gray-700 leading-3.5">評価損益</div>
          <div className="flex ml-auto font-bold">
            <label className={`${profitOrLoss.color}`}>
              <div className={`flex items-center`}>
                <div className="text-sm">{`${profitOrLoss.amount}`}</div>
                <div className="ml-1">円</div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

const CardPickerItem = ({
  amount: amount_,
  profitOrLoss: profitOrLoss_,
  isHiddenAsset,
  canToggleAssetVisibility,
  toggleAssetVisibility: toggleAssetVisibility_,
  isOutOfDateBrokers,
  brokers,
  refetchBrokers,
  isAuthenticatedMoneytree,
  isLinkedMoneytree,
  initialCardType,
}: TotalCardPickerItemProps): React.ReactElement => {
  const { hideModal, isShowModal, showModal } = useIsShowModal();
  const hiddenStr = '---';
  const {
    amount,
    profitOrLoss,
  }: {
    amount: string;
    profitOrLoss: string;
  } =
    isHiddenAsset || initialCardType.key !== 0
      ? {
          amount: hiddenStr,
          profitOrLoss: hiddenStr,
        }
      : {
          amount: commaFormat(amount_),
          profitOrLoss: addSignCommaFormat(profitOrLoss_),
        };
  const toggleAssetVisibility = (): void => {
    if (!canToggleAssetVisibility) return;
    toggleAssetVisibility_();
  };
  const viewProps: CardPickerItemViewProps = {
    cautionElement:
      isOutOfDateBrokers || (!isAuthenticatedMoneytree && isLinkedMoneytree) ? (
        <img
          className="mr-2"
          src="/static/images/icon/icon-caution.svg"
          height="16"
          width="16"
          alt=""
          onClick={showModal}
        />
      ) : (
        undefined
      ),
    amount,
    onClickEye: stopPropagation(toggleAssetVisibility),
    profitOrLoss: {
      amount: profitOrLoss,
      color:
        isHiddenAsset || initialCardType.key !== 0
          ? ''
          : amountToAmountColor(profitOrLoss_),
    },
    eyeClassName: `${getEyeStyle(isHiddenAsset)} ${getPointerStyle(
      canToggleAssetVisibility
    )}`,
  };
  return (
    <>
      <CardPickerItemView {...viewProps} />
      {isShowModal && (
        <CooperationStoppedModal
          {...{
            isShowModal,
            hideModal,
            brokers,
            isAuthenticatedMoneytree,
            isLinkedMoneytree,
          }}
        />
      )}
    </>
  );
};

const GroupCardPickerItem = ({
  amount: amount_,
  profitOrLoss: profitOrLoss_,
  groupKey,
  groupName,
  initialCardType,
}: GroupCardPickerItemProps & {
  initialCardType: AssetBundleType;
}): React.ReactElement => {
  const groupKeyToColor = (groupKey: GroupAssetKey): string => {
    return `bg-group-${groupKey}`;
  };
  const groupColor = groupKeyToColor(groupKey);
  const group = 'グループ';

  const isCardSelected = groupKey === initialCardType.key;
  const hiddenStr = '---';
  const amount = isCardSelected ? commaFormat(amount_) : hiddenStr;
  const profitOrLoss = isCardSelected
    ? addSignCommaFormat(profitOrLoss_)
    : hiddenStr;
  const profitOrLossColor = isCardSelected
    ? amountToAmountColor(profitOrLoss_)
    : '';
  return (
    <>
      <div className="slick-item flex flex-col min-w-assetcard px-2 h-full shadow-card">
        <div className="flex items-center slick-item-header">
          <div className="flex items-center h-8 w-1/2">
            <div className="flex flex-1 items-end bg-white rounded-tl-md h-30p">
              <div className="lg:max-w-card-l max-w-card-s absolute top-1 ml-4 text-sm font-bold truncate">
                {groupName}
              </div>
            </div>
          </div>
          <img
            className="tab-rounded h-30p"
            height="30"
            width="25"
            src="/static/images/tab-rounded.svg"
            alt=""
          />
          <div
            className={`flex items-center justify-center ${groupColor} px-4 h-4.5 rounded-full text-white text-xs ml-auto truncate`}>
            <img
              className="mr-2"
              height="10"
              width="12"
              src="/static/images/icon/icon-folder.svg"
              alt=""
            />
            {group}
          </div>
        </div>
        <div className="flex-1 bg-white py-6 px-4 rounded-bl-md rounded-br-md rounded-tr-md slick-item-body">
          <div className="flex justify-end leading-6 font-bold px-2">
            <div className="text-base mr-1">{amount}</div>
            <div className="text-xs">円</div>
          </div>
          <div className="flex items-center text-xs pt-1 px-2 border-t border-bluegray-300">
            <div className="text-gray-700 leading-3.5">評価損益</div>
            <div
              className={`flex items-center ml-auto font-bold ${profitOrLossColor}`}>
              <div className="text-sm mr-1">{profitOrLoss}</div>
              <div className="text-xs">円</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const AddGroupCardPickerItem = ({
  addGroup,
}: AddGroupCardPickerItemProps): React.ReactElement => {
  return (
    <>
      <div className="addgroup-card">
        <div
          className="slick-item flex flex-col items-center justify-center bg-white rounded-md py-3 px-4 min-w-assetcard h-full shadow-card"
          onClick={addGroup}>
          <div className="flex">
            <div className="text-xl text-blue-700 font-bold leading-6 pr-3.5">
              ＋
            </div>
            <div className="text-base text-blue-700 font-bold leading-6">
              グループを追加する
            </div>
          </div>
          <img
            src="/static/images/home/group.svg"
            width="144"
            height="24"
            alt="グループを追加する"
          />
          <div className="text-xs text-bluegray-500 leading-4.5 text-center">
            連携した資産を目的に合わせて
            <br />
            自由にグルーピング＆データ化しよう
          </div>
        </div>
      </div>
    </>
  );
};

type CardPickerDomainProps = {
  setCardType: React.Dispatch<React.SetStateAction<AssetBundleType>>;
  initialCardType: AssetBundleType;
  addGroup: () => void;
};

type CardPickerProps = CardPickerDomainProps & {
  homeUser: UserState;
  brokers: api.GetUserBrokerResultItem[];
  asset: GetUserAssetResult;
  accountGroup: AccountGroupProps[];
  isOutOfDateBrokers: boolean;
  refetchBrokers: <T>(_: T) => void;
  toggleAssetVisibility: () => void;
};

const cardPickerCommonSliderSettings = {
  infinite: false,
  variableWidth: true,
  focusOnSelect: true,
  slidesToScroll: 1,
  slidesToShow: 1,
  responsive: [
    {
      breakpoint: 1080, //ブレイクポイントを指定
      settings: {
        variableWidth: true,
        centerMode: true,
      },
    },
  ],
};
const cardPickerCommonClassName = 'flex items-center';

const cardTypesToArray = <T,>(
  func: (cardType: AssetBundleType) => T,
  groupAssets: Omit<GroupCardPickerItemProps, 'onSelect'>[]
): T[] => {
  return [
    func({
      type: '資産総額',
      key: 0,
    }),
    ...groupAssets.map(item =>
      func({
        type: 'グループ資産',
        key: item.groupKey,
      })
    ),
    func({
      type: 'グループ追加',
      key: 9999,
    }),
  ];
};

export const CardPicker = ({
  setCardType,
  addGroup,
  asset,
  accountGroup,
  initialCardType,
  homeUser,
  brokers,
  isOutOfDateBrokers,
  refetchBrokers,
  toggleAssetVisibility,
}: CardPickerProps): React.ReactElement => {
  const totalAsset: Omit<TotalCardPickerItemProps, 'onSelect'> = {
    amount: asset.TotalAssetAmount ?? 0,
    profitOrLoss: asset.TotalProfitOrLoss ?? 0,
    isHiddenAsset: homeUser.isHiddenAsset.value,
    canToggleAssetVisibility:
      homeUser.isHiddenAsset.toggleAssetVisibilityState === 'IS_READY',
    toggleAssetVisibility,
    isOutOfDateBrokers,
    brokers: brokers,
    refetchBrokers: async () => await refetchBrokers({}),
    isAuthenticatedMoneytree: homeUser.isAuthenticatedMoneytree,
    isLinkedMoneytree: homeUser.isLinkedMoneytree,
    initialCardType,
  };

  const groupAssets: Omit<
    GroupCardPickerItemProps,
    'onSelect'
  >[] = accountGroup.map(item => {
    const key = Number(item.id) as GroupAssetKey;
    return {
      groupKey: key,
      amount:
        asset.TotalAssetAmount && key === initialCardType.key
          ? asset.TotalAssetAmount
          : 0,
      groupName: item.name,
      profitOrLoss:
        asset.TotalProfitOrLoss && key === initialCardType.key
          ? asset.TotalProfitOrLoss
          : 0,
    };
  });

  const onChanges: (() => void)[] = cardTypesToArray(
    cardType => (): void => setCardType(cardType),
    groupAssets
  );
  const initialSlideIndex = cardTypesToArray(
    cardType => cardType,
    groupAssets
    // eslint-disable-next-line array-callback-return
  ).reduce((acc, current, index): number => {
    switch (current.type) {
      case 'グループ追加':
        if (initialCardType.type !== 'グループ追加') return acc;
        return index;
      case '資産総額':
      case 'グループ資産':
        if (initialCardType.type === 'グループ追加') return acc;
        switch (current.type) {
          case 'グループ資産':
            if (initialCardType.type !== 'グループ資産') return acc;
            return initialCardType.key === current.key ? index : acc;
          case '資産総額':
            if (initialCardType.type !== '資産総額') return acc;
            return initialCardType.key === current.key ? index : acc;
        }
    }
  }, 0);
  const sliderSettings: Settings = {
    ...cardPickerCommonSliderSettings,
    centerMode: true,
    afterChange: index => {
      onChanges[index]();
    },
    initialSlide: initialSlideIndex,
  };

  return (
    <>
      <Slider {...sliderSettings} className={cardPickerCommonClassName}>
        <CardPickerItem key="total-asset" {...totalAsset} />
        {groupAssets.map((item, i) => (
          <GroupCardPickerItem
            {...{ ...item, initialCardType }}
            key={`group-asset-${i + 1}`}
          />
        ))}
        <AddGroupCardPickerItem key="add-group" addGroup={addGroup} />
      </Slider>
    </>
  );
};

export const AccountAggregatingCardPicker = () => {
  const eyeClassName = `${getEyeStyle(true)} ${getPointerStyle(false)}`;
  return (
    <>
      <div className={cardPickerCommonClassName + ' justify-center'}>
        <div className="slick-slider">
          <div className="slick-slide block">
            <CardPickerItemView
              {...{
                amount: '---',
                profitOrLoss: {
                  amount: '---',
                  color: '',
                },
                onClickEye: () => {
                  return;
                },
                eyeClassName,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};
